// web/src/lib/invoice-totals.ts
//
// Pure-function totals math that replicates
// Allmark.Application.InvoicesPayments.InvoiceComposer.Compose exactly:
//   - half-away-from-zero rounding to 2dp
//   - credit-line negation
//   - delivery charge always GST-applicable
//
// Invalid lines (per validateLine) contribute 0 to the running totals so the
// caller never has to scrub NaN out of mid-typing state.

export interface LineDraft {
  description: string;
  quantity: string;
  unitPriceExGst: string;
  discountPct: string;
  gstApplicable: boolean;
  isCredit: boolean;
}

export interface InvoiceTotals {
  subtotal: number;
  gst: number;
  total: number;
}

export interface TotalsInput {
  lines: LineDraft[];
  deliveryExGst: number;
}

const GST_RATE = 0.1;

/**
 * Round to 2dp using half-away-from-zero, matching C# MidpointRounding.AwayFromZero.
 * Note: plain Math.round uses banker's-style for binary floats; we work in cents.
 */
export function round2(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const cents = v * 100;
  // Math.round in JS rounds .5 toward +∞ (so -0.5 → 0, not -1). We want
  // away-from-zero: sign-flip negatives, round, sign-restore.
  const rounded = v >= 0
    ? Math.round(cents)
    : -Math.round(-cents);
  return rounded / 100;
}

export function validateLine(line: LineDraft): boolean {
  if (line.description.trim().length === 0) return false;
  const q = Number(line.quantity);
  const p = Number(line.unitPriceExGst);
  const d = Number(line.discountPct);
  if (!Number.isFinite(q) || q <= 0) return false;
  if (!Number.isFinite(p) || p < 0) return false;
  if (!Number.isFinite(d) || d < 0 || d > 100) return false;
  return true;
}

/**
 * Returns the line's ex-GST amount, or null if the line is invalid.
 * Negated when isCredit. Rounded to 2dp.
 */
export function computeLineAmount(line: LineDraft): number | null {
  if (!validateLine(line)) return null;
  const q = Number(line.quantity);
  const p = Number(line.unitPriceExGst);
  const d = Number(line.discountPct);
  const raw = q * p * (1 - d / 100);
  const signed = line.isCredit ? -raw : raw;
  return round2(signed);
}

export function computeInvoiceTotals(input: TotalsInput): InvoiceTotals {
  let subtotal = 0;
  let gst = 0;
  for (const line of input.lines) {
    const ex = computeLineAmount(line);
    if (ex === null) continue;
    subtotal += ex;
    if (line.gstApplicable) gst += round2(ex * GST_RATE);
  }
  if (input.deliveryExGst !== 0) {
    subtotal += input.deliveryExGst;
    gst += round2(input.deliveryExGst * GST_RATE);
  }
  return {
    subtotal: round2(subtotal),
    gst: round2(gst),
    total: round2(subtotal + gst),
  };
}
